'use client';
import { useState, useEffect } from 'react';

// Device type constants
const DEVICE_TYPES = {
  MOBILE: 'mobile',
  TABLET: 'tablet',
  DESKTOP: 'desktop'
};

const DESKTOP_BREAKPOINT = 992;
const TABLET_BREAKPOINT = 768;

/**
 * Hook to detect device type (mobile, tablet, desktop)
 * based on window width and user agent
 * @returns {Object} Device type information
 */
const useDeviceType = () => {
  // Start with null to ensure consistent SSR/client rendering
  const [deviceType, setDeviceType] = useState(null);
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    // Mark that we're on the client
    setIsClient(true);
    
    const getDeviceType = () => {
      const width = window.innerWidth;
      if (width >= DESKTOP_BREAKPOINT) return DEVICE_TYPES.DESKTOP;
      if (width >= TABLET_BREAKPOINT) return DEVICE_TYPES.TABLET;
      return DEVICE_TYPES.MOBILE;
    };

    // Set initial device type
    setDeviceType(getDeviceType());

    const handleResize = () => {
      setDeviceType(getDeviceType());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // During SSR or initial client render, return desktop as default
  const currentDeviceType = deviceType || DEVICE_TYPES.DESKTOP;

  return {
    deviceType: currentDeviceType,
    isMobile: currentDeviceType === DEVICE_TYPES.MOBILE,
    isTablet: currentDeviceType === DEVICE_TYPES.TABLET,
    isDesktop: currentDeviceType === DEVICE_TYPES.DESKTOP,
    isClient, // Useful for conditional rendering
    // Touch device detection (more reliable than just width-based)
    isTouchDevice: typeof navigator !== 'undefined' && 
      (('ontouchstart' in window) || 
       (navigator.maxTouchPoints > 0) || 
       (navigator.msMaxTouchPoints > 0)),
  };
};

export default useDeviceType;